// @ts-strict-ignore
import * as React from 'react';
import Icon, { BranchesOutlined, PullRequestOutlined } from '@ant-design/icons';
import {
    CodeType,
    CommitItem,
    FeatureItemCodeStatus,
    PRItem,
    PullRequestStackInfo,
    PullRequestStatus,
    ResolvedFeatureAffiliation,
} from '../../types';
import MergeIcon from '../icons/MergeIcon';

export const stackInfoTooltipText: (stackInfo: PullRequestStackInfo, targetedBranch?: string) => string = (
    stackInfo,
    targetedBranch,
) => {
    return (
        `Part of a stack of ${stackInfo.size} pull requests (this one is #${stackInfo.position} from the bottom) targeting the ${stackInfo.baseBranch} branch` +
        (targetedBranch ? `. It targets ${targetedBranch}, the branch below it in the stack.` : '')
    );
};

export const isCommit = (code: PRItem | CommitItem): code is CommitItem => {
    if (!code) return null;
    return (code as CommitItem).type === CodeType.Commit;
};

export const featureAffiliationsFromCode: (code: PRItem | CommitItem) => ResolvedFeatureAffiliation[] = (code) => {
    if (isCommit(code)) {
        if (code.sourcePR?.featureAffiliations) {
            return code.sourcePR.featureAffiliations;
        }
        if (code.unsquashedChildCommitForPR?.featureAffiliations) {
            return code.unsquashedChildCommitForPR.featureAffiliations;
        }
    } else {
        if (code.featureAffiliations) {
            return code.featureAffiliations;
        }
    }
    return [];
};

export const iconForStatus: (status: FeatureItemCodeStatus, codeItem: PRItem | CommitItem) => React.ReactNode = (
    status: FeatureItemCodeStatus,
    codeItem: PRItem | CommitItem,
) => {
    const MergeIconSized = () => {
        return <MergeIcon width={14} height={14} />;
    };

    switch (status) {
        case FeatureItemCodeStatus.Merged:
        case FeatureItemCodeStatus.CherryPickMerged:
        case FeatureItemCodeStatus.CherryPickSkipped:
        case FeatureItemCodeStatus.CherryPickManuallyResolved:
            return <Icon component={MergeIconSized} />;
        case FeatureItemCodeStatus.OpenPR:
        case FeatureItemCodeStatus.CherryPickOpen:
        case FeatureItemCodeStatus.CherryPickClosed:
            return <PullRequestOutlined />;
        case FeatureItemCodeStatus.Unmerged:
        case FeatureItemCodeStatus.CherryPickPending:
        case FeatureItemCodeStatus.CherryPickQueued:
            return <BranchesOutlined />;
        case FeatureItemCodeStatus.CherryPickFailed:
            if (
                (codeItem as PRItem)?.pullRequestStatus &&
                (codeItem as PRItem).pullRequestStatus === PullRequestStatus.Open
            ) {
                return <PullRequestOutlined />;
            } else {
                return <BranchesOutlined />;
            }
        default:
            return null;
    }
};

export const getLabelsFromFeatureAffiliations: (
    featureAffiliations: ResolvedFeatureAffiliation[],
) => ResolvedFeatureAffiliation[] = (featureAffiliations) => {
    return featureAffiliations.filter((fa) => fa.affiliationType.indexOf('label') !== -1);
};
